import { t } from "@lingui/core/macro";
import type { ReactNode } from "react";
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type Path,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from "react-hook-form";
import { GoogleAddressAutocompleteInput } from "@/components/address/GoogleAddressAutocompleteInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ACTIVE_ADDRESS_COUNTRY,
  postalCodeHint,
  US_STATES,
} from "@/lib/config/address";

type AddressFieldShape = {
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
};

interface AddressAutocompleteFieldsProps<TFormValues extends FieldValues & AddressFieldShape> {
  idPrefix?: string;
  control: Control<TFormValues>;
  register: UseFormRegister<TFormValues>;
  setValue: UseFormSetValue<TFormValues>;
  clearErrors?: UseFormClearErrors<TFormValues>;
  errors: FieldErrors<TFormValues>;
  inputClassName: string;
  selectTriggerClassName: string;
  labels?: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  placeholders?: {
    address?: string;
    city?: string;
  };
}

function getErrorMessage(error: unknown): string | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return undefined;
}

function FormField({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <Label htmlFor={id} className="text-sm font-medium text-(--color-text-primary)">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-(--color-danger-text)">{error}</p>}
    </div>
  );
}

export function AddressAutocompleteFields<TFormValues extends FieldValues & AddressFieldShape>({
  idPrefix,
  control,
  register,
  setValue,
  clearErrors,
  errors,
  inputClassName,
  selectTriggerClassName,
  labels,
  placeholders,
}: AddressAutocompleteFieldsProps<TFormValues>) {
  const addressName = "addressLine1" as Path<TFormValues>;
  const cityName = "city" as Path<TFormValues>;
  const stateName = "state" as Path<TFormValues>;
  const postalName = "postalCode" as Path<TFormValues>;
  const countryName = "country" as Path<TFormValues>;
  const latitudeName = "latitude" as Path<TFormValues>;
  const longitudeName = "longitude" as Path<TFormValues>;

  const addressError = getErrorMessage((errors as Record<string, unknown>).addressLine1);
  const postalError = getErrorMessage((errors as Record<string, unknown>).postalCode);

  return (
    <>
      <FormField
        label={labels?.address ?? t`Address`}
        id={idPrefix ? `${idPrefix}-address1` : undefined}
        error={addressError}
      >
        <Controller
          name={addressName}
          control={control}
          render={({ field }) => (
            <GoogleAddressAutocompleteInput
              id={idPrefix ? `${idPrefix}-address1` : undefined}
              value={(field.value as string | undefined) ?? ""}
              onChange={(nextValue) => {
                setValue(addressName, nextValue as never, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                setValue(latitudeName, undefined as never);
                setValue(longitudeName, undefined as never);
              }}
              onAddressSelected={(address) => {
                const normalizedZip = (address.postalCode ?? "").replace(/\D/g, "").slice(0, 5);
                setValue(addressName, address.addressLine1 as never, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
                setValue(cityName, (address.city ?? "") as never, { shouldDirty: true, shouldTouch: true });
                setValue(stateName, (address.state ?? "") as never, { shouldDirty: true, shouldTouch: true });
                setValue(postalName, normalizedZip as never, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
                setValue(countryName, ACTIVE_ADDRESS_COUNTRY as never, { shouldDirty: true });
                setValue(latitudeName, address.latitude as never, { shouldDirty: true });
                setValue(longitudeName, address.longitude as never, { shouldDirty: true });

                clearErrors?.(addressName);
                clearErrors?.(cityName);
                clearErrors?.(stateName);
                clearErrors?.(postalName);
              }}
              placeholder={placeholders?.address ?? t`Enter or search address...`}
              className={`${inputClassName} ${addressError ? "border-(--color-danger)" : ""}`}
            />
          )}
        />
      </FormField>

      <div className="flex gap-3">
        <FormField
          label={labels?.city ?? t`City`}
          id={idPrefix ? `${idPrefix}-city` : undefined}
        >
          <Input
            id={idPrefix ? `${idPrefix}-city` : undefined}
            placeholder={placeholders?.city ?? t`City...`}
            {...register(cityName)}
            className={inputClassName}
          />
        </FormField>

        <FormField
          label={labels?.state ?? t`State / Province`}
          id={idPrefix ? `${idPrefix}-state` : undefined}
        >
          <Controller
            name={stateName}
            control={control}
            render={({ field }) => (
              <Select
                value={(field.value as string | undefined) || "none"}
                onValueChange={(nextValue) => {
                  const value = nextValue === "none" ? "" : nextValue;
                  setValue(stateName, value as never, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  clearErrors?.(stateName);
                }}
              >
                <SelectTrigger
                  id={idPrefix ? `${idPrefix}-state` : undefined}
                  className={selectTriggerClassName}
                >
                  <SelectValue placeholder={t`Select state`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t`Select state`}</SelectItem>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormField>
      </div>

      <FormField
        label={labels?.postalCode ?? t`ZIP Code`}
        id={idPrefix ? `${idPrefix}-postalCode` : undefined}
        error={postalError}
      >
        <Controller
          name={postalName}
          control={control}
          render={({ field }) => (
            <Input
              id={idPrefix ? `${idPrefix}-postalCode` : undefined}
              placeholder={postalCodeHint(ACTIVE_ADDRESS_COUNTRY)}
              value={(field.value as string | undefined) ?? ""}
              onChange={(event) => {
                const normalized = event.target.value.replace(/\D/g, "").slice(0, 5);
                setValue(postalName, normalized as never, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
                clearErrors?.(postalName);
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
              className={`${inputClassName} ${postalError ? "border-(--color-danger)" : ""}`}
            />
          )}
        />
      </FormField>
    </>
  );
}